<template>
  <section class="enrollee-add-workbench sba-slide-in" aria-labelledby="enrollee-add-title">
    <div class="enrollee-add-toolbar">
      <div class="enrollee-add-copy">
        <h2 id="enrollee-add-title">Add manual enrollee</h2>
        <p>Enter the member details for the current monthly register.</p>
      </div>
      <div class="enrollee-add-toolbar-actions">
        <router-link :to="{ name: 'enrollee' }" class="enrollee-cancel-link">
          <X :size="16" aria-hidden="true" />
          <span>Close</span>
        </router-link>
        <div class="enrollee-coverage-meta" aria-label="Coverage period">
          <span>Coverage period</span>
          <strong>{{ monthCoverageLabel }}</strong>
        </div>
      </div>
    </div>

    <form class="enrollee-add-form" @submit.prevent="handleSubmit">
      <div class="enrollee-scheme-summary" :class="{ 'has-error': !currentScheme }" aria-live="polite">
        <ShieldCheck v-if="currentScheme" :size="18" aria-hidden="true" />
        <AlertCircle v-else :size="18" aria-hidden="true" />
        <div>
          <span>Scheme</span>
          <strong>{{ currentSchemeName }}</strong>
        </div>
      </div>

      <div class="enrollee-form-grid">
        <label class="enrollee-field">
          <span>Enrollee name</span>
          <input v-model.trim="form.name" type="text" autocomplete="name" required />
        </label>

        <label class="enrollee-field">
          <span>Enrollee ID</span>
          <input
            v-model.trim="form.enrollee_number"
            type="text"
            autocomplete="off"
            required
            @blur="normalizeId"
          />
          <small v-if="normalizedPreview && normalizedPreview !== form.enrollee_number">
            Saves as {{ normalizedPreview }}
          </small>
        </label>

        <label class="enrollee-field">
          <span>Sex</span>
          <select v-model="form.sex" class="browser-default" required>
            <option disabled value="">Select sex</option>
            <option value="m">Male</option>
            <option value="f">Female</option>
          </select>
        </label>

        <label class="enrollee-field">
          <span>Date of birth</span>
          <input v-model="form.dob" type="date" required />
        </label>
      </div>

      <fieldset class="enrollee-type-group">
        <legend>Member type</legend>
        <div class="enrollee-type-options">
          <button
            v-for="option in typeOptions"
            :key="option.value"
            type="button"
            :class="{ active: form.type === option.value }"
            :aria-pressed="form.type === option.value"
            @click="form.type = option.value"
          >
            {{ option.label }}
          </button>
        </div>
      </fieldset>

      <div class="enrollee-error" v-if="error" role="alert">
        <AlertCircle :size="18" aria-hidden="true" />
        <span>{{ error }}</span>
      </div>

      <div class="enrollee-success" v-if="createdEnrollee" role="status">
        <CheckCircle2 :size="18" aria-hidden="true" />
        <div>
          <strong>{{ createdEnrollee.name }}</strong>
          <span>{{ createdEnrollee.enrollee_number }} is active for {{ monthCoverageLabel }}.</span>
        </div>
      </div>

      <div class="enrollee-add-submit-row">
        <button type="submit" class="enrollee-submit-button" :disabled="!isFormReady">
          <LoaderCircle v-if="isSubmitting" class="is-spinning" :size="17" aria-hidden="true" />
          <UserPlus v-else :size="17" aria-hidden="true" />
          <span>{{ submitLabel }}</span>
        </button>
      </div>
    </form>
  </section>
</template>

<script>
import { computed, reactive, ref } from "vue";
import { useStore } from "vuex";
import { AlertCircle, CheckCircle2, LoaderCircle, ShieldCheck, UserPlus, X } from "@lucide/vue";
import postItem from "@/composables/postItem";
import { normalizeEnrolleeNumber } from "@/composables/enrolleeNumber";
import nativeUi from "@/ui/native-ui";

export default {
  name: "enrolleeAdd",
  components: {
    AlertCircle,
    CheckCircle2,
    LoaderCircle,
    ShieldCheck,
    UserPlus,
    X,
  },
  setup() {
    const store = useStore();
    const isSubmitting = ref(false);
    const error = ref(null);
    const createdEnrollee = ref(null);

    const form = reactive({
      name: "",
      enrollee_number: "",
      sex: "",
      dob: "",
      type: "",
    });

    const typeOptions = [
      { label: "Principal", value: "p" },
      { label: "Spouse", value: "s" },
      { label: "Dependant", value: "d" },
    ];

    const currentScheme = computed(() => {
      const schemes = Array.isArray(store.state.user?.schemes)
        ? store.state.user.schemes
        : [];

      return schemes.find((scheme) => scheme.curr) || schemes[0] || null;
    });

    const currentSchemeName = computed(() => {
      return currentScheme.value?.name || "No active scheme selected";
    });

    const normalizedPreview = computed(() => {
      return normalizeEnrolleeNumber(form.enrollee_number);
    });

    const monthCoverageLabel = computed(() => {
      const today = new Date();
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      const options = { month: "short", day: "numeric", year: "numeric" };

      return `${start.toLocaleDateString(undefined, options)} - ${end.toLocaleDateString(undefined, options)}`;
    });

    const isFormReady = computed(() => {
      return Boolean(
        currentScheme.value &&
        form.name.trim() &&
        normalizedPreview.value &&
        form.sex &&
        form.dob &&
        form.type &&
        !isSubmitting.value,
      );
    });

    const submitLabel = computed(() => {
      return isSubmitting.value ? "Adding..." : "Add enrollee";
    });

    const normalizeId = () => {
      form.enrollee_number = normalizedPreview.value;
    };

    const resetForm = () => {
      form.name = "";
      form.enrollee_number = "";
      form.sex = "";
      form.dob = "";
      form.type = "";
    };

    const handleSubmit = async () => {
      if (!isFormReady.value) {
        return;
      }

      normalizeId();
      isSubmitting.value = true;
      error.value = null;
      createdEnrollee.value = null;

      const payload = {
        name: form.name.trim(),
        enrollee_number: form.enrollee_number,
        sex: form.sex,
        dob: form.dob,
        type: form.type,
        scheme_id: currentScheme.value.id,
      };

      const { res, err } = await postItem("enrollee/manual", payload);

      isSubmitting.value = false;

      if (res.value?.data) {
        createdEnrollee.value = res.value.data;
        nativeUi.toast({ html: "Enrollee added" });
        resetForm();
        return;
      }

      error.value = err.value || "Unable to add enrollee.";
    };

    return {
      form,
      typeOptions,
      currentScheme,
      currentSchemeName,
      normalizedPreview,
      monthCoverageLabel,
      isFormReady,
      submitLabel,
      isSubmitting,
      error,
      createdEnrollee,
      normalizeId,
      handleSubmit,
    };
  },
};
</script>

<style scoped>
.enrollee-add-workbench {
  background: transparent;
  color: var(--ui-foreground);
  min-width: 0;
}

.enrollee-add-toolbar {
  align-items: flex-end;
  border-bottom: 1px solid var(--ui-border);
  display: flex;
  gap: 2rem;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding-bottom: 1.25rem;
}

.enrollee-add-copy {
  min-width: 0;
}

.enrollee-add-copy h2 {
  color: var(--ui-foreground);
  font-size: 1.125rem;
  font-weight: 650;
  letter-spacing: -0.015em;
  line-height: 1.35;
  margin: 0;
}

.enrollee-add-copy p {
  color: var(--ui-muted-foreground);
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0.25rem 0 0;
}

.enrollee-add-toolbar-actions {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 1.5rem;
}

.enrollee-cancel-link {
  align-items: center;
  background: transparent;
  border: 1px solid color-mix(in srgb, var(--ui-destructive) 24%, var(--ui-border));
  border-radius: 999px;
  color: var(--ui-destructive);
  display: inline-flex;
  font-size: 0.75rem;
  font-weight: 600;
  gap: 0.35rem;
  min-height: 2rem;
  padding: 0 0.65rem;
  text-decoration: none;
  transition: background-color 160ms ease, border-color 160ms ease, color 160ms ease;
}

.enrollee-cancel-link:hover {
  background: color-mix(in srgb, var(--ui-destructive) 8%, transparent);
  border-color: color-mix(in srgb, var(--ui-destructive) 42%, var(--ui-border));
  color: var(--ui-destructive);
}

.enrollee-cancel-link:focus-visible {
  outline: 2px solid var(--ui-ring);
  outline-offset: 2px;
}

.enrollee-coverage-meta {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: 0.15rem;
  text-align: right;
}

.enrollee-coverage-meta span,
.enrollee-scheme-summary span,
.enrollee-field > span,
.enrollee-type-group legend {
  color: var(--ui-muted-foreground);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0;
}

.enrollee-coverage-meta strong,
.enrollee-scheme-summary strong {
  color: var(--ui-foreground);
  font-size: 0.875rem;
  font-weight: 600;
}

.enrollee-add-form {
  max-width: 58rem;
}

.enrollee-scheme-summary {
  align-items: center;
  background: var(--ui-muted);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius);
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  min-height: 3.75rem;
  padding: 0.7rem 0.9rem;
}

.enrollee-scheme-summary > svg {
  color: var(--ui-muted-foreground);
  flex: 0 0 auto;
}

.enrollee-scheme-summary > div {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.enrollee-scheme-summary.has-error {
  background: #fff6f6;
  border-color: #ffcdd2;
}

.enrollee-scheme-summary.has-error > svg {
  color: #c62828;
}

.enrollee-form-grid {
  display: grid;
  gap: 1.25rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.enrollee-field {
  color: var(--ui-foreground);
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  min-width: 0;
}

.enrollee-field > span {
  color: var(--ui-foreground);
}

.enrollee-field input,
.enrollee-field select {
  background: var(--ui-background) !important;
  border: 1px solid var(--ui-border) !important;
  border-radius: var(--ui-radius) !important;
  box-shadow: none !important;
  box-sizing: border-box;
  color: var(--ui-foreground);
  font-family: var(--ui-font-sans);
  font-size: 0.875rem;
  height: 2.75rem !important;
  margin: 0 !important;
  outline: none;
  padding: 0 0.8rem !important;
  width: 100%;
}

.enrollee-field input:focus,
.enrollee-field select:focus {
  border-color: var(--ui-ring) !important;
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--ui-ring) 18%, transparent) !important;
}

.enrollee-field small {
  color: var(--ui-muted-foreground);
  font-size: 0.75rem;
  line-height: 1.4;
}

.enrollee-type-group {
  border: 0;
  margin: 1.5rem 0 0;
  min-width: 0;
  padding: 0;
}

.enrollee-type-group legend {
  color: var(--ui-foreground);
  margin-bottom: 0.55rem;
  padding: 0;
}

.enrollee-type-options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.enrollee-type-options button {
  background: var(--ui-background);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius);
  color: var(--ui-foreground);
  cursor: pointer;
  font-family: var(--ui-font-sans);
  font-size: 0.8125rem;
  font-weight: 600;
  min-height: 2.5rem;
  padding: 0 0.9rem;
}

.enrollee-type-options button:hover {
  background: var(--ui-muted);
}

.enrollee-type-options button.active {
  background: var(--ui-primary);
  border-color: var(--ui-primary);
  color: var(--ui-primary-foreground);
}

.enrollee-type-options button:focus-visible,
.enrollee-submit-button:focus-visible {
  outline: 2px solid var(--ui-ring);
  outline-offset: 2px;
}

.enrollee-error,
.enrollee-success {
  align-items: flex-start;
  border: 1px solid;
  border-radius: var(--ui-radius);
  display: flex;
  font-size: 0.8125rem;
  gap: 0.65rem;
  margin-top: 1.5rem;
  padding: 0.75rem 0.9rem;
}

.enrollee-error {
  background: #fff6f6;
  border-color: #ffcdd2;
  color: #b71c1c;
}

.enrollee-success {
  background: #eefaf8;
  border-color: #a7dcd5;
  color: #00695c;
}

.enrollee-success strong,
.enrollee-success span {
  display: block;
}

.enrollee-add-submit-row {
  border-top: 1px solid var(--ui-border);
  display: flex;
  justify-content: flex-end;
  margin-top: 1.75rem;
  padding-top: 1.25rem;
}

.enrollee-submit-button {
  align-items: center;
  background: var(--ui-primary);
  border: 1px solid var(--ui-primary);
  border-radius: var(--ui-radius);
  color: var(--ui-primary-foreground);
  cursor: pointer;
  display: inline-flex;
  font-family: var(--ui-font-sans);
  font-size: 0.8125rem;
  font-weight: 600;
  gap: 0.5rem;
  justify-content: center;
  min-height: 2.625rem;
  padding: 0 1rem;
}

.enrollee-submit-button:hover:not(:disabled) {
  filter: brightness(0.95);
}

.enrollee-submit-button:disabled {
  background: var(--ui-muted);
  border-color: var(--ui-border);
  color: var(--ui-muted-foreground);
  cursor: not-allowed;
}

.is-spinning {
  animation: enrolleeSpin 0.8s linear infinite;
}

@media (max-width: 760px) {
  .enrollee-add-toolbar {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.8rem;
  }

  .enrollee-add-toolbar-actions {
    justify-content: space-between;
    width: 100%;
  }

  .enrollee-coverage-meta {
    text-align: left;
  }

  .enrollee-form-grid {
    grid-template-columns: 1fr;
  }

  .enrollee-submit-button {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .is-spinning {
    animation: none;
  }
}

@keyframes enrolleeSpin {
  to {
    transform: rotate(360deg);
  }
}
</style>
